import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { User, AuthContextType, RegisterData } from '../types';
import { authAPI, ensureCsrfToken } from '../services/api';
import { useAutoLogout } from '../hooks/useAutoLogout';
import { clearVmsWorkspacePersistence } from './VmsWorkspaceContext';

function cacheUserInSession(user: User): void {
  sessionStorage.setItem('user', JSON.stringify(user));
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** SessionStorage key: set on login, cleared on tab close. Missing = tab was closed, treat as logout. */
const SESSION_MARKER_KEY = 'session_marker';

function clearAuthSessionStorage(): void {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('permissions');
  sessionStorage.removeItem('mfa_session_token');
  sessionStorage.removeItem('selectedSystem');
  sessionStorage.removeItem(SESSION_MARKER_KEY);
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const userIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    userIdRef.current = user?.id;
  }, [user?.id]);

  const clearLocalAuthState = useCallback(() => {
    const userId = userIdRef.current;
    if (userId != null) {
      clearVmsWorkspacePersistence(userId);
    }
    setUser(null);
    setToken(null);
    clearAuthSessionStorage();
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      clearLocalAuthState();
      window.location.href = '/login';
    }
  }, [clearLocalAuthState]);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      if (!sessionStorage.getItem(SESSION_MARKER_KEY)) {
        if (cancelled) return;
        setUser(null);
        setToken(null);
        clearAuthSessionStorage();
        const path = window.location.pathname;
        const isPublic =
          path === '/login' ||
          path === '/register' ||
          path.startsWith('/forget-password') ||
          path.startsWith('/forgot-password');
        if (!isPublic) {
          try {
            await authAPI.logout();
          } catch {
            // ignore
          }
          window.location.href = '/login';
        }
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.verify();
        if (cancelled) return;
        if (response.success && response.data?.user) {
          setUser(response.data.user);
          setToken('cookie');
          cacheUserInSession(response.data.user);
          await ensureCsrfToken();
        } else {
          try {
            await authAPI.logout();
          } catch {
            // ignore
          }
          clearLocalAuthState();
          window.location.href = '/login';
          return;
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setToken(null);
        }
      }
      if (!cancelled) {
        setLoading(false);
      }
    };

    void initAuth();

    return () => {
      cancelled = true;
    };
  }, [clearLocalAuthState]);

  const login = useCallback(async (email: string, password: string, sessionToken?: string) => {
    try {
      setLoading(true);
      const payload = {
        email: email,
        password: password,
      };
      const response = await authAPI.login(payload);
      if (response.success && response.data) {
        const { user: userData } = response.data;

        clearVmsWorkspacePersistence(userData.id);
        setUser(userData);
        setToken('cookie');

        cacheUserInSession(userData);
        sessionStorage.setItem(SESSION_MARKER_KEY, '1');

        if (sessionToken) {
          sessionStorage.setItem('mfa_session_token', sessionToken);
        }
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch {
      throw new Error('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async (userData: RegisterData) => {
    try {
      setLoading(true);
      const response = await authAPI.register(userData);

      if (response.success && response.data) {
        const { user: newUser } = response.data;

        clearVmsWorkspacePersistence(newUser.id);
        setUser(newUser);
        setToken('cookie');

        cacheUserInSession(newUser);
        sessionStorage.setItem(SESSION_MARKER_KEY, '1');
      } else {
        throw new Error(response.error || 'Registration failed');
      }
    } catch {
      throw new Error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.verify();
      if (response.success && response.data?.user) {
        const userData = response.data.user;
        setUser(userData);
        cacheUserInSession(userData);
        await ensureCsrfToken();
      }
    } catch {
      // Keep current user on verify failure (e.g. network)
    }
  }, []);

  const setAuthenticated = useCallback((newUser: User, _token: string) => {
    clearVmsWorkspacePersistence(newUser.id);
    setUser(newUser);
    setToken('cookie');
    cacheUserInSession(newUser);
    sessionStorage.removeItem('selectedSystem');
    sessionStorage.setItem(SESSION_MARKER_KEY, '1');
  }, []);

  useAutoLogout({
    isAuthenticated: !!user && !!token,
    logout,
  });

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      token,
      login,
      register,
      logout,
      loading,
      refreshUser,
      setAuthenticated,
    }),
    [user, token, login, register, logout, loading, refreshUser, setAuthenticated],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
